import { Kicker } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { type AdminGateFailure, type AdminOverview, adminOrgsHref } from '../lib/admin';
import { moneyFromDecimal } from '../lib/time';

// Platform radar stat tiles (admin.developerz.ai). Presenter only — the
// AdminOverview container passes the resolved `GET /v1/admin/overview` rollup.
// Spec §5 stats band: each labelled sub-band (reliability / fleet / queue / spend /
// billing) is a `.metric-grid--5` of card tiles, mirroring the mock's fleet Stats
// but with the radar's richer real content. Accounts, users and runs left for
// `AdminTrendBands` (#6715): they carry a 30-day history, these do not.
//
// Billing (#5726) is the same cards, two optional fields wider: a `note` sub-line
// (the hive band's `.metric-grid__note`) and an `href` that wraps the VALUE in a
// classless anchor — `_base.scss` gives every `a` inherited colour and no
// underline, so the card is unchanged and no CSS is added. Its accessible name
// leads with the label, so a reader hears "Paying accounts 31", not a bare "31".
// No revenue figure, by decision: Stripe is not live (#2771), and a list-price
// estimate would be wrong in a way nobody notices.

// Rates arrive as ratios in [0, 1]; render them as a percentage so the escalation
// figure reads off the radar (#761) rather than as a raw fraction.
// ponytail: the copy's locale (`t.locale`); a `lib/time.ts` number seam would retire these.
const PCT = new Intl.NumberFormat(t.locale, { style: 'percent', maximumFractionDigits: 1 });
const NUM = new Intl.NumberFormat(t.locale);

/** One tile: an uppercase mono label + a mono value, optionally a sub-line and a drill-in. */
type Tile = {
  readonly label: string;
  readonly value: string | number;
  readonly note?: string;
  readonly href?: string;
};

/** One labelled band of tiles. */
type Band = { readonly title: string; readonly tiles: readonly Tile[] };

/**
 * Humanize an escalation cause `category` (a machine id like `low_confidence`)
 * via `admin.cause.<category>`, falling back to the raw category for a cause the
 * catalog hasn't been taught yet — a new `@developerz/domain` category must show
 * *something*, not a `⟦…⟧` miss marker or a blank tile.
 */
function causeLabel(category: string): string {
  const key = `admin.cause.${category}`;
  return t.has(key) ? t(key) : category;
}

/**
 * One unmet GA-readiness check as a tile: the reason humanized via
 * `admin.gate.<code>` (fallback to the raw code so a new gate code never blanks),
 * valued by the observed number — a percentage for the rate checks, a count for
 * the sample/model checks.
 */
function gateFailureTile(f: AdminGateFailure): Tile {
  const key = `admin.gate.${f.code}`;
  const isRate = f.code === 'clean_terminal_below' || f.code === 'failed_above';
  return {
    label: t.has(key) ? t(key) : f.code,
    value: isRate ? PCT.format(f.observed) : NUM.format(f.observed),
  };
}

/** Fold the overview rollup into the ordered bands the radar renders. */
function bands(o: AdminOverview): readonly Band[] {
  return [
    {
      // #761: the escalation-rate figure + its DARK GA-readiness verdict. Headline
      // rates + sample size + window, the machine PASS / NOT READY tile and (when
      // not ready) one tile per unmet check, then one tile per escalation cause
      // (same Total-plus-breakdown shape as the accounts band's per-tier tiles).
      // Zero-sample renders the rates at 0% / 0 and reads NOT READY — never a
      // crash and never a divide-by-zero artefact.
      title: t('admin.band_reliability'),
      tiles: [
        { label: t('admin.stat_escalation_rate'), value: PCT.format(o.reliability.escalationRate) },
        { label: t('admin.stat_clean_rate'), value: PCT.format(o.reliability.cleanTerminalRate) },
        { label: t('admin.stat_failed_rate'), value: PCT.format(o.reliability.failedRate) },
        { label: t('admin.stat_sample'), value: NUM.format(o.reliability.sampleSize) },
        {
          label: t('admin.stat_window'),
          value: t('admin.stat_window_value', { days: o.reliability.windowDays }),
        },
        {
          label: t('admin.stat_ga_ready'),
          value: o.reliability.gaGate.pass ? t('admin.ga_pass') : t('admin.ga_not_ready'),
        },
        // Only when NOT ready: name each unmet check so the verdict says WHY.
        ...(o.reliability.gaGate.pass ? [] : o.reliability.gaGate.failures.map(gateFailureTile)),
        ...o.reliability.escalationCauses.map((c) => ({
          label: causeLabel(c.category),
          value: c.n,
        })),
      ],
    },
    {
      title: t('admin.band_fleet'),
      tiles: [
        { label: t('admin.stat_total'), value: o.fleet.total },
        { label: t('admin.stat_online'), value: o.fleet.online },
        { label: t('admin.stat_offline'), value: o.fleet.offline },
        { label: t('admin.stat_pending'), value: o.fleet.pending },
        { label: t('admin.stat_revoked'), value: o.fleet.revoked },
      ],
    },
    {
      title: t('admin.band_queue'),
      tiles: [
        { label: t('admin.stat_depth'), value: o.queue.depth },
        { label: t('admin.stat_pending'), value: o.queue.pending },
        { label: t('admin.stat_assigned'), value: o.queue.assigned },
        { label: t('admin.stat_in_progress'), value: o.queue.in_progress },
        { label: t('admin.stat_failed'), value: o.queue.failed },
      ],
    },
    {
      title: t('admin.band_spend'),
      tiles: [{ label: t('admin.stat_mtd'), value: moneyFromDecimal(o.spend.mtd_usd) }],
    },
    {
      // Each number drills into the org list narrowed to exactly what it counts;
      // seats and CI slots ride the paying link: `paying` is `seats > 0 OR
      // purchased_ci_slots > 0`, so both numbers are bought by exactly those accounts.
      title: t('admin.band_billing'),
      tiles: [
        {
          label: t('admin.stat_paying_accounts'),
          value: o.billing.paying_accounts,
          note: tPlural('admin.stat_paid_seats', o.billing.paid_seats, {
            count: String(o.billing.paid_seats),
          }),
          href: adminOrgsHref('paying'),
        },
        {
          label: t('admin.stat_ci_slots'),
          value: o.billing.purchased_ci_slots,
          href: adminOrgsHref('paying'),
        },
        {
          label: t('admin.stat_paused_accounts'),
          value: o.billing.paused_accounts,
          href: adminOrgsHref('paused'),
        },
        {
          label: t('admin.stat_comped_ai_devs'),
          value: o.billing.comped_ai_devs,
          note: tPlural('admin.stat_comped_accounts', o.billing.comped_accounts, {
            count: String(o.billing.comped_accounts),
          }),
          href: adminOrgsHref('comped'),
        },
      ],
    },
  ];
}

export function AdminStatBand(props: { overview: AdminOverview }): JSX.Element {
  return (
    <For each={bands(props.overview)}>
      {(band) => (
        <section class="admin-band">
          <h2>
            <Kicker as="span">{band.title}</Kicker>
          </h2>
          <ul class="metric-grid metric-grid--5" aria-label={band.title}>
            <For each={band.tiles}>
              {(tile) => (
                <li class="card">
                  <div class="card__content">
                    <p class="metric-grid__label">{tile.label}</p>
                    <p class="metric-grid__value">
                      <Show when={tile.href} fallback={tile.value}>
                        {(href) => (
                          <a href={href()} aria-label={`${tile.label} ${tile.value}`}>
                            {tile.value}
                          </a>
                        )}
                      </Show>
                    </p>
                    <Show when={tile.note}>
                      {(note) => <p class="metric-grid__note">{note()}</p>}
                    </Show>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </section>
      )}
    </For>
  );
}
